import { z } from 'zod';
/**
 * Chat REST DTO — Phase 7 v1.1 / Plan 07-02 (CHAT-04).
 *
 * 엔드포인트:
 *
 *   **POST /api/v1/chat/messages**  (session + ChatAccessGuard + ChatRateLimiter)
 *     - 로그인 사용자가 LOOP 네이티브 채팅을 입력.
 *     - body: { streamerId, content }
 *     - 성공: 201 { id, createdAt, shadowed }.
 *     - rate limited: 429 + `retry-after-ms` 헤더 + { code: 'rate_limited', retryAfterMs }.
 *     - gate fail: 403 + { code: 'forbidden_chat_native_disabled' | 'forbidden_chat_anonymous'
 *       | 'forbidden_by_tier' | 'forbidden_streamer_not_found' }.
 *
 *   **GET /api/v1/chat/messages?roomId=&cursor=&limit=** (public)
 *     - 최근 메시지 backfill — Plan 07-04 LiveChatOverlay 가 초기 로딩 시 호출.
 *     - 익명 viewer 는 shadowed 메시지 제외, 로그인 사용자는 본인 것만 포함.
 *
 * **계약 불변**:
 *   - streamerId · roomId 는 uuid / `live:{streamerId}` 포맷.
 *   - content 는 1~500자 (server-side 500 절단 + Zod 검증).
 *   - shadowed 플래그는 **작성자 본인 응답**에만 true 로 노출. 목록 조회는 shadow-ban 본인
 *     노출 정책 (is_shadowed=false OR author=viewer) 을 서버 레벨에서 적용하므로 응답 DTO
 *     에는 shadowed 를 싣지 않음 (privacy — 타인이 shadow 여부 탐지 불가).
 *
 * **LOOP→외부 역송출 금지 (T12)**:
 *   본 계약은 LOOP 내부 `/chat` namespace 에만 broadcast. 외부 플랫폼(chzzk/soop/youtube)
 *   으로 역송출하는 경로는 schema · service · event 모두에서 부재. 본 DTO 에도 publish
 *   관련 필드 일절 없음.
 *
 * Wave 1 범위: 응답은 Zod 런타임 검증 없이 타입만 소비 (community.rest.ts 와 동일 정책).
 */
// ---------------------------------------------------------------------------
// POST /api/v1/chat/messages
// ---------------------------------------------------------------------------
/**
 * POST 요청 body — 로그인 사용자가 streamerId 에 대해 채팅 한 건을 입력.
 *
 * - `streamerId`: 대상 스트리머 uuid. ChatAccessGuard 가 streamers.chat_native_enabled
 *   + chat_native_required_tier_id 를 검증.
 * - `content`: 1~500자. 서버가 `normalizeForFilter` 로 재계산하여 DB 저장, 클라이언트 값
 *   신뢰 안 함. 501자 이상은 400 reject.
 */
export const postChatMessageRequestSchema = z.object({
    streamerId: z.string().uuid(),
    content: z.string().min(1).max(500),
});
/**
 * POST 응답 — shadowed 가 true 여도 201 정상 응답. 본인에게만 보이는 shadow-ban 정책
 * (Phase 3 Community 재사용 — PostController.createPost 가 동일 패턴).
 *
 * - `id`: chat_messages.id (uuid).
 * - `createdAt`: timestamptz ISO-8601 문자열.
 * - `shadowed`: 작성자 본인에게만 true 노출. UI 는 "검토 대기" 배지로 표시 가능.
 */
export const postChatMessageResponseSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    shadowed: z.boolean(),
});
/** Rate limiter 초과 시 429 응답 body. */
export const chatRateLimitedResponseSchema = z.object({
    code: z.literal('rate_limited'),
    retryAfterMs: z.number().int().min(1).max(10_000),
});
// ---------------------------------------------------------------------------
// GET /api/v1/chat/messages (backfill)
// ---------------------------------------------------------------------------
/**
 * GET 쿼리 — 최근 메시지 backfill. Plan 07-04 LiveChatOverlay 초기 로딩 용도.
 *
 * - `roomId`: `live:{streamerId}` 포맷 문자열 (varchar 100).
 * - `cursor`: 이전 페이지 마지막 메시지의 createdAt ISO — before 필터.
 * - `limit`: 1~200, default 50.
 */
export const listChatMessagesQuerySchema = z.object({
    roomId: z
        .string()
        .min(1)
        .max(100)
        .regex(/^live:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, {
        message: 'roomId must be live:{streamerId} with uuid',
    }),
    cursor: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
/**
 * GET 응답 DTO — 시청자 카운트 경량화. shadowed 플래그는 privacy 보호로 클라이언트에
 * 노출하지 않는다 (shadow-ban 필터링은 서버가 이미 적용).
 */
export const chatMessageDtoSchema = z.object({
    id: z.string().uuid(),
    roomId: z.string(),
    externalPlatform: z.enum(['chzzk', 'soop', 'youtube', 'loop']),
    authorDisplayName: z.string(),
    content: z.string(),
    createdAt: z.string().datetime(),
});
export const listChatMessagesResponseSchema = z.object({
    items: z.array(chatMessageDtoSchema),
    /** 다음 페이지 커서 (가장 오래된 아이템의 createdAt ISO). hasMore=false 시 null. */
    nextCursor: z.string().datetime().nullable(),
});
//# sourceMappingURL=chat.rest.js.map